import React, { useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";
import type { AuthUser } from "./types";
import { authService } from "../../features/auth/services/auth.api";
import { refreshSession } from "../../api/apiFetch";
import { setAccessToken } from "./tokenStore";
import { broadcastSession, broadcastLogout, onAuthBroadcast } from "./broadcast";
import { hasSessionHint, setSessionHint, clearSessionHint } from "./sessionHint";
import { NotificationProvider } from "../NotificationContext";

/**
 * Owns the ONLY React state for `user`. The access token itself is never
 * kept in state here -- it lives solely in tokenStore.ts (a plain in-memory
 * module variable) so apiFetch's imperative interceptor code can read/write
 * it without a React dependency. This component is only responsible for:
 * establishing a session (sign in, silent refresh on mount, cross-tab sync)
 * and tearing one down (sign out).
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ── Cross-tab sync ──────────────────────────────────────────────────────
  // The refresh-token cookie is shared across every tab in this browser, but
  // the access token is per-tab in-memory state -- without this, a sibling
  // tab has no way to learn that this tab just rotated it (or logged out).
  useEffect(() => {
    return onAuthBroadcast((msg) => {
      if (msg.type === "session") {
        setAccessToken(msg.accessToken);
        setUser(msg.user);
        setSessionHint();
        setLoading(false);
      } else if (msg.type === "logout") {
        setAccessToken(null);
        setUser(null);
        clearSessionHint();
      }
    });
  }, []);

  // apiFetch dispatches this when an opportunistic refresh (triggered by some
  // unrelated request's 401) comes back empty-handed -- the session is over,
  // apiFetch is already redirecting to /login, this just keeps React state
  // from momentarily rendering as "still logged in" before that nav lands.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener("arteria:session-expired", onExpired);
    return () => window.removeEventListener("arteria:session-expired", onExpired);
  }, []);

  // ── Silent refresh on mount ──────────────────────────────────────────────
  // Every full page load starts with an empty in-memory access token by
  // design (see tokenStore.ts) -- this is what re-establishes the session
  // from the HttpOnly refresh-token cookie, e.g. after a page refresh or
  // reopening the browser. withRefreshLock serializes this across tabs that
  // happen to load at the same instant, since they all share the same cookie.
  useEffect(() => {
    // One-time cleanup of the pre-refactor localStorage session, if any is
    // still lying around from before this deploy -- it's never read anymore,
    // this just removes the stale garbage.
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    } catch {
      /* localStorage unavailable */
    }

    // No prior session on this device (or it was already cleared) -- skip
    // the network round trip entirely instead of firing a refresh call we
    // already know has nothing to restore.
    if (!hasSessionHint()) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setRefreshing(true);
      try {
        // refreshSession() is the single, shared entry point every caller in
        // the app uses (this effect AND apiFetch's own 401-triggered retry) --
        // it never throws, and its module-level in-flight promise means that
        // however many times this effect body runs (React StrictMode
        // intentionally double-invokes mount effects in dev) or however many
        // other components race it, only one real POST /auth/refresh ever
        // goes out; every caller just awaits the same result. It already
        // applies the access token / session hint / cross-tab broadcast as
        // soon as it resolves, regardless of which caller is awaiting it --
        // this effect only needs to apply the result to its own `user` state.
        const result = await refreshSession();
        if (cancelled) return;
        if (result?.user) {
          setUser(result.user);
        }
      } finally {
        if (!cancelled) {
          setRefreshing(false);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────────

  const setSession = (nextUser: AuthUser, accessToken: string) => {
    setAccessToken(accessToken);
    setUser(nextUser);
    setSessionHint();
    broadcastSession(nextUser, accessToken);
  };

  const signIn = async (email: string, password: string) => {
    const res = await authService.signIn(email, password);
    setSession(res.user, res.token);
  };

  const clearLocalSession = () => {
    setAccessToken(null);
    setUser(null);
    clearSessionHint();
    broadcastLogout();
  };

  const signOut = async () => {
    try {
      await authService.signOut();
    } catch {
      // Best-effort -- still tear down local state even if the network call fails.
    } finally {
      clearLocalSession();
    }
  };

  const signOutAll = async () => {
    try {
      await authService.signOutAll();
    } finally {
      clearLocalSession();
    }
  };

  const refreshProfile = async () => {
    setLoading(true);
    try {
      const res = await authService.getCurrentUser();
      setUser(res.user);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, refreshing, signIn, signOut, signOutAll, refreshProfile, setSession }}
    >
      <NotificationProvider userId={user?.id}>{children}</NotificationProvider>
    </AuthContext.Provider>
  );
};
